import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Download,
  Mail,
  Save,
  Loader2,
  Link2,
  XCircle,
  Archive,
  ExternalLink,
  PackageCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  projectsApi,
  locationsApi,
  teamApi,
  callSheetApi,
} from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";

import { ConfirmationsBanner } from "@/components/callsheet/ConfirmationsBanner";
import { RecipientsPreview } from "@/components/callsheet/RecipientsPreview";
import { HeaderSection } from "@/components/callsheet/HeaderSection";
import { LocationsSection } from "@/components/callsheet/LocationsSection";
import { TalentSection } from "@/components/callsheet/TalentSection";
import { CrewSection } from "@/components/callsheet/CrewSection";
import { ClientSection } from "@/components/callsheet/ClientSection";
import { CallSheetPreview } from "@/components/callsheet/CallSheetPreview";
import { EmailDialog } from "@/components/callsheet/EmailDialog";
import { PastCallSheetsDialog } from "@/components/callsheet/PastCallSheetsDialog";
import { WrapShootDialog } from "@/components/callsheet/WrapShootDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DayTabs } from "@/components/callsheet/DayTabs";

export default function CallSheetPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const dayParam = searchParams.get("day");

  const [project, setProject] = useState(null);
  const [days, setDays] = useState([]);
  const [archivedDays, setArchivedDays] = useState([]);
  const [activeDayId, setActiveDayId] = useState(null);
  const [locations, setLocations] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [cancelShootOpen, setCancelShootOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [wrapShootOpen, setWrapShootOpen] = useState(false);
  const [pastOpen, setPastOpen] = useState(false);
  const navigate = useNavigate();

  const isAdmin = project?.my_role === "admin";
  const callSheet = days.find((d) => d.id === activeDayId) || null;

  const selectDay = (dayId) => {
    setActiveDayId(dayId);
    // Sync URL so the active day is shareable / survives refresh
    const next = new URLSearchParams(searchParams);
    next.set("day", dayId);
    setSearchParams(next, { replace: true });
  };

  const reload = async () => {
    try {
      const [p, dayList, archivedList, locs, t] = await Promise.all([
        projectsApi.get(id),
        callSheetApi.listForProject(id),
        callSheetApi.listArchivedForProject(id),
        locationsApi.list(),
        teamApi.list(),
      ]);
      setProject(p);
      setDays(dayList);
      setArchivedDays(archivedList);
      setLocations(locs);
      setTeam(t);
      // Choose active day: URL param > previously selected > first
      const pick =
        dayList.find((d) => d.id === dayParam) ||
        dayList.find((d) => d.id === activeDayId) ||
        dayList[0];
      if (pick) {
        setActiveDayId(pick.id);
        if (pick.id !== dayParam) {
          const next = new URLSearchParams(searchParams);
          next.set("day", pick.id);
          setSearchParams(next, { replace: true });
        }
      } else {
        // No active days left — clear the active id (empty-state path).
        setActiveDayId(null);
      }
    } catch {
      toast.error("Failed to load call sheet");
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await reload();
      setLoading(false);
    })();
  }, [id]);

  const handleField = (k, v) => {
    setDays((prev) =>
      prev.map((d) => (d.id === activeDayId ? { ...d, [k]: v } : d))
    );
  };

  const handleSave = async () => {
    if (!callSheet) return;
    setSaving(true);
    try {
      const payload = {
        day_label: callSheet.day_label || "",
        shoot_date: callSheet.shoot_date,
        day_of: callSheet.day_of,
        weather: callSheet.weather,
        sunrise: callSheet.sunrise,
        sunset: callSheet.sunset,
        general_call: callSheet.general_call,
        shooting_call: callSheet.shooting_call,
        est_wrap: callSheet.est_wrap,
        director_contact: callSheet.director_contact,
        production_notes: callSheet.production_notes,
        talent: callSheet.talent || [],
        crew: callSheet.crew || [],
        clients: callSheet.clients || [],
      };
      const updated = await callSheetApi.updateDay(id, callSheet.id, payload);
      setDays((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      toast.success("Call sheet saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!callSheet) return;
    setDownloading(true);
    try {
      if (isAdmin) await handleSave();
      const url = callSheetApi.pdfUrlForDay(id, callSheet.id);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      const label = callSheet.day_label?.trim() || `Day_${callSheet.day_number}`;
      const fname = `${(project?.name || "call_sheet").replace(/\s+/g, "_")}_${label.replace(/\s+/g, "_")}_call_sheet.pdf`;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (!callSheet?.id) return;
    const url = `${window.location.origin}/share/call-sheet/${callSheet.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — opens this specific day");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  if (loading || !project) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );
  }

  // No active call sheets — two possible empty states:
  //  1. All days have been archived ("clean slate" post-wrap) — nudge the
  //     user toward "View Past Call Sheets" and let them schedule a new one.
  //  2. Never had a shoot scheduled — the original "Schedule first shoot" CTA.
  if (!callSheet) {
    const allWrapped = archivedDays.length > 0;
    return (
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-20">
        <div
          data-testid={allWrapped ? "call-sheet-wrapped-state" : "call-sheet-empty-state"}
          className="border border-zinc-800 rounded-md bg-zinc-950/60 p-10 text-center max-w-xl mx-auto"
        >
          <div className="text-overline text-zinc-500 mb-3">Call Sheet</div>
          {allWrapped ? (
            <>
              <PackageCheck
                className="w-8 h-8 text-emerald-400 mx-auto mb-3"
                strokeWidth={1.5}
              />
              <h2 className="font-heading text-2xl font-light text-zinc-100 mb-2">
                All shoots wrapped
              </h2>
              <p className="text-sm text-zinc-500 mb-6">
                Every shoot day for this project has been marked complete.
                Past call sheets are archived and still accessible below.
              </p>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <Button
                  data-testid="view-past-call-sheets-btn-empty"
                  onClick={() => setPastOpen(true)}
                  className="bg-zinc-900 border border-zinc-800 text-zinc-100 hover:bg-zinc-800 rounded-sm"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  View Past Call Sheets ({archivedDays.length})
                </Button>
                {isAdmin && (
                  <Button
                    data-testid="schedule-another-shoot-btn"
                    onClick={async () => {
                      try {
                        const created = await callSheetApi.addDay(id, {});
                        toast.success("Shoot day created");
                        await reload();
                        if (created?.id) {
                          setActiveDayId(created.id);
                          const next = new URLSearchParams(searchParams);
                          next.set("day", created.id);
                          setSearchParams(next, { replace: true });
                        }
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Failed to schedule shoot");
                      }
                    }}
                    className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
                  >
                    Schedule another shoot
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              <h2 className="font-heading text-2xl font-light text-zinc-100 mb-2">
                No shoot scheduled
              </h2>
              <p className="text-sm text-zinc-500 mb-6">
                {isAdmin
                  ? "This project doesn't have a shoot day yet. Schedule one when you're ready — the plan will sync automatically."
                  : "There's no shoot day scheduled for this project yet."}
              </p>
              {isAdmin && (
                <Button
                  data-testid="schedule-first-shoot-btn"
                  onClick={async () => {
                    try {
                      const created = await callSheetApi.addDay(id, {});
                      toast.success("Shoot day created");
                      await reload();
                      if (created?.id) {
                        setActiveDayId(created.id);
                        const next = new URLSearchParams(searchParams);
                        next.set("day", created.id);
                        setSearchParams(next, { replace: true });
                      }
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Failed to schedule shoot");
                    }
                  }}
                  className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
                >
                  Schedule first shoot
                </Button>
              )}
            </>
          )}
        </div>

        <PastCallSheetsDialog
          open={pastOpen}
          onOpenChange={setPastOpen}
          projectId={id}
          archivedDays={archivedDays}
          canEdit={isAdmin}
          onRestored={reload}
        />
      </div>
    );
  }

  const primaryLocation =
    (project.location_ids && project.location_ids.length
      ? project.location_ids
      : project.location_id
        ? [project.location_id]
        : []
    )
      .map((lid) => locations.find((l) => l.id === lid))
      .find((l) => l && l.address) || null;

  return (
    <div>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-6 pb-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-overline text-zinc-500">
          Call Sheet
          {days.length > 1 && (
            <span className="ml-2 text-zinc-600 normal-case tracking-normal">
              · {days.length} days
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <button
                data-testid="save-call-sheet-btn"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-zinc-900 border border-zinc-800 text-zinc-100 hover:bg-zinc-800 rounded-sm disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
              </button>
              <button
                data-testid="copy-share-link-btn"
                onClick={handleCopyShareLink}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-transparent border border-zinc-800 text-zinc-300 hover:bg-zinc-900 rounded-sm"
              >
                <Link2 className="w-4 h-4" /> Copy Share Link
              </button>
              <button
                data-testid="email-call-sheet-btn"
                onClick={() => setEmailOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-transparent border border-zinc-800 text-zinc-300 hover:bg-zinc-900 rounded-sm"
              >
                <Mail className="w-4 h-4" /> Email PDF
              </button>
              {project.status !== "completed" && project.status !== "archived" && (
                <button
                  data-testid="wrap-shoot-btn"
                  onClick={() => setWrapShootOpen(true)}
                  title="Wrap the shoot — archives every remaining day (project stays Active until Complete Project is clicked)"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-transparent border border-emerald-800/60 text-emerald-300 hover:bg-emerald-950/40 hover:border-emerald-600 rounded-sm transition-colors"
                >
                  <PackageCheck className="w-4 h-4" /> Wrap Shoot
                </button>
              )}
              <button
                data-testid="cancel-shoot-btn"
                onClick={() => setCancelShootOpen(true)}
                title="Cancel this shoot — removes the call sheet and linked Production plan task"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-transparent border border-rose-900/60 text-rose-300 hover:bg-rose-950/40 hover:border-rose-700 rounded-sm transition-colors"
              >
                <XCircle className="w-4 h-4" /> Cancel Shoot
              </button>
            </>
          )}
          {archivedDays.length > 0 && (
            <button
              data-testid="view-past-call-sheets-btn"
              onClick={() => setPastOpen(true)}
              title="View wrapped shoot days"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-transparent border border-zinc-800 text-zinc-300 hover:bg-zinc-900 rounded-sm"
            >
              <Archive className="w-4 h-4" /> Past Call Sheets ({archivedDays.length})
            </button>
          )}
          {project?.gear_project_url ? (
            <a
              data-testid="view-in-gear-btn"
              href={project.gear_project_url}
              target="_blank"
              rel="noopener noreferrer"
              title="Open this project in Gear"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-transparent border border-sky-800/60 text-sky-300 hover:bg-sky-950/40 hover:border-sky-600 rounded-sm transition-colors"
            >
              <ExternalLink className="w-4 h-4" /> View in Gear
            </a>
          ) : null}
          <button
            data-testid="download-call-sheet-btn"
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-amber-500 text-black hover:bg-amber-400 rounded-sm disabled:opacity-50"
          >
            <Download className="w-4 h-4" />{" "}
            {downloading ? "Generating…" : "Download PDF"}
          </button>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <DayTabs
          days={days}
          activeDayId={activeDayId}
          onSelect={selectDay}
          canEdit={isAdmin}
          projectId={id}
          onDaysChanged={(fresh, newActiveId) => {
            setDays(fresh);
            if (newActiveId) {
              setActiveDayId(newActiveId);
              const next = new URLSearchParams(searchParams);
              next.set("day", newActiveId);
              setSearchParams(next, { replace: true });
            }
          }}
        />
      </div>

      <div className={`max-w-screen-2xl mx-auto px-6 lg:px-10 py-6 ${
        isAdmin ? "grid grid-cols-1 xl:grid-cols-[1fr,420px] gap-8" : "flex justify-center"
      }`}>
        {isAdmin && (
          <div className="space-y-8">
            <ConfirmationsBanner
              confirmations={callSheet.confirmations || []}
              onRefresh={reload}
            />
            <RecipientsPreview
              team={team}
              talent={callSheet.talent || []}
              crew={callSheet.crew || []}
              clients={callSheet.clients || []}
              onOpenEmail={() => setEmailOpen(true)}
            />
            <HeaderSection
              callSheet={callSheet}
              onField={handleField}
              primaryLocation={primaryLocation}
              totalDays={days.length}
            />
            <LocationsSection
              project={project}
              locations={locations}
              onLocationUpdated={reload}
            />
            <TalentSection
              team={team}
              items={callSheet.talent || []}
              onChange={(items) => handleField("talent", items)}
              confirmations={callSheet.confirmations || []}
            />
            <CrewSection
              team={team}
              items={callSheet.crew || []}
              onChange={(items) => handleField("crew", items)}
              confirmations={callSheet.confirmations || []}
            />
            <ClientSection
              team={team}
              items={callSheet.clients || []}
              onChange={(items) => handleField("clients", items)}
              confirmations={callSheet.confirmations || []}
            />

            <section className="rounded-md border border-zinc-800 bg-zinc-900/60 p-6">
              <div className="text-overline text-zinc-400 mb-3">Production Notes</div>
              <Textarea
                data-testid="cs-notes-input"
                value={callSheet.production_notes || ""}
                onChange={(e) => handleField("production_notes", e.target.value)}
                placeholder="Any notes for the day — call protocol, what to wear, parking instructions…"
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 min-h-[100px]"
              />
            </section>
          </div>
        )}

        <aside className={isAdmin ? "xl:sticky xl:top-24 self-start" : "max-w-2xl w-full"}>
          <div className="text-overline text-zinc-500 mb-3">
            {isAdmin ? "Live Preview" : "Call Sheet"}
          </div>
          <CallSheetPreview
            project={project}
            callSheet={callSheet}
            locations={locations}
            team={team}
          />
        </aside>
      </div>

      <EmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        projectId={id}
        dayId={callSheet.id}
        project={project}
        team={team}
        callSheet={callSheet}
        totalDays={days.length}
        onSendStart={() => handleSave()}
        onSent={reload}
      />

      {/* Cancel Shoot — removes this call sheet + cascade-deletes its linked
          Production plan task. If it's the last day, we redirect back to the
          project shell since the Call Sheets tab will be empty. */}
      <Dialog open={cancelShootOpen} onOpenChange={setCancelShootOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md" data-testid="cancel-shoot-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-light flex items-center gap-2">
              <XCircle className="w-5 h-5 text-rose-400" />
              Cancel this shoot?
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              This permanently removes <span className="text-zinc-300">{callSheet.day_label?.trim() || `Day ${callSheet.day_number}`}</span>
              {" "}({callSheet.shoot_date || "no date"}), its call sheet (talent,
              crew, schedule, confirmations), and the linked Production task in
              the project plan. Public share links for this day will stop
              working.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              onClick={() => setCancelShootOpen(false)}
              disabled={cancelling}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
              data-testid="cancel-shoot-keep-btn"
            >
              Keep shoot
            </Button>
            <Button
              data-testid="cancel-shoot-confirm-btn"
              disabled={cancelling}
              onClick={async () => {
                setCancelling(true);
                try {
                  await callSheetApi.removeDay(id, callSheet.id);
                  toast.success("Shoot cancelled");
                  const fresh = await callSheetApi.listForProject(id);
                  setCancelShootOpen(false);
                  if (fresh.length === 0) {
                    // No more shoot days — bounce back to the project shell
                    navigate(`/projects/${id}`);
                  } else {
                    const fallback = fresh[0];
                    setDays(fresh);
                    setActiveDayId(fallback.id);
                    const next = new URLSearchParams(searchParams);
                    next.set("day", fallback.id);
                    setSearchParams(next, { replace: true });
                  }
                } catch (e) {
                  const detail = e?.response?.data?.detail;
                  toast.error(detail?.message || detail || "Failed to cancel shoot");
                } finally {
                  setCancelling(false);
                }
              }}
              className="bg-rose-600 hover:bg-rose-500 text-white"
            >
              {cancelling ? "Cancelling…" : "Cancel Shoot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wrap Shoot — archives every remaining active day (no status
          change; that happens via Complete Project in the header). */}
      <WrapShootDialog
        open={wrapShootOpen}
        onOpenChange={setWrapShootOpen}
        projectId={id}
        onWrapped={async () => {
          setWrapShootOpen(false);
          await reload();
        }}
      />

      <PastCallSheetsDialog
        open={pastOpen}
        onOpenChange={setPastOpen}
        projectId={id}
        archivedDays={archivedDays}
        canEdit={isAdmin}
        onRestored={reload}
      />
    </div>
  );
}
